import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { CheckIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import * as React from "react";

export type FacetedFilterOption = {
  label: string;
  value: string;
  total?: number;
  icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
};

export function ButtonFacetedFilter({
  title,
  options,
  selected,
  compacted = true,
  onChange = () => {},
}: {
  title?: string;
  options: FacetedFilterOption[];
  selected: Array<string>;
  compacted?: boolean;
  onChange?: (selected: Array<string>) => void;
}) {
  const { t } = useTranslation();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircledIcon className="w-4 h-4 mr-2" />
          {title}
          {selected?.length > 0 && (
            <>
              <Separator orientation="vertical" className="h-4 mx-2" />
              <Badge
                variant="secondary"
                className="px-1 font-normal rounded-sm lg:hidden"
              >
                {selected.length}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {compacted && selected.length > 2 ? (
                  <Badge
                    variant="secondary"
                    className="px-1 font-normal rounded-sm"
                  >
                    {t("components.buttonFacetedFilter.selected", {
                      count: selected.length,
                    })}
                  </Badge>
                ) : (
                  options
                    .filter((option) => selected.includes(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="px-1 font-normal rounded-sm"
                      >
                        {option.label}{" "}
                        {option.total !== undefined &&
                          option.total > 0 &&
                          `(${option.total})`}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty className="p-3">
              {t("components.buttonFacetedFilter.noOptionsFound")}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        onChange(selected.filter((v) => v !== option.value));
                      } else {
                        onChange([...selected, option.value]);
                      }
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <option.icon className={cn("w-4 h-4 mr-2 text-muted-foreground", option.iconClassName)} />
                    )}
                    <span>{option.label}</span>
                    {option.total !== undefined && option.total > 0 && (
                      <span className="flex items-center justify-center w-4 h-4 ml-auto font-mono text-xs">
                        {option.total}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selected.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      selected = [];
                      onChange(selected);
                    }}
                    className="justify-center text-center"
                  >
                    {t("components.buttonFacetedFilter.resetFilters")}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
